import React from "react";

const MethodologyScoring: React.FC = () => (
  <div className="space-y-6 text-sm md:text-base leading-relaxed text-[#380C1E]/90 font-light">
    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        5. Scoring
      </h2>
      <p className="mb-3">
        All rules below are the rules implemented in instrument version 4.
        Nothing is weighted, hidden, or adjusted.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        5.1 Domain scores
      </h3>
      <p className="mb-3">
        Each tap awards one point to the domain behind the chosen statement. The
        unchosen statement receives nothing. Each domain's raw score is its
        count across the 30 screens, range 0 to 10. For display, the raw score
        is multiplied by 10 and shown as a percentage (7 picks = 70 percent).
        Because each domain is scored out of its own maximum of 10, the six
        percentages do not, and are not expected to, sum to 100. The six raw
        scores always sum to 30.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        5.2 Primary and runner-up
      </h3>
      <p className="mb-3">
        The primary domain is the one with the highest raw score. The runner-up
        is the second highest. The report presents all six domains ranked from
        highest to lowest, not in framework order, because the ranking is the
        result.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        5.3 Blend rule
      </h3>
      <p className="mb-3">
        If the runner-up is within two points of the primary (a difference of 0,
        1, or 2), the report presents the result as a blend of two domains. The
        primary domain still leads the report. This rule exists because a
        two-point gap on a 10-point scale, produced by 30 binary choices, is not
        a confident separation, and the instrument should not pretend otherwise.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        5.4 Tie rule
      </h3>
      <p className="mb-3">
        If two domains tie for the highest score, they are resolved by
        head-to-head record. Those two domains met on exactly two screens (once
        under each stem). If one of them won both, it is the primary. If they
        split one and one, the result is treated as a full blend and the report
        leads with whichever domain comes first in framework order (Beliefs,
        Belonging, Boundaries, Business, Budget, Body), because that is the
        domain addressed earlier in the curriculum. Version 4 does not specify a
        separate rule for a three-way or larger tie; in that case the
        head-to-head rule is applied where it is decisive and framework order
        where it is not, and this is noted as a design gap to be closed in the
        next revision.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        5.5 Convergence indicator
      </h3>
      <p className="mb-3">
        If the primary domain earned at least one point under Stem One and at
        least one point under Stem Two, the report states that the domain
        appeared "both in what's been heavy and in what you're hoping for." If
        the primary's points all came from one stem, the report shows nothing.
        This is the only place the two-stem design produces a visible output
        beyond the scores themselves, and it is deliberately modest: convergence
        increases confidence in the attribution, but its absence is not reported
        as a failure.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        5.6 Pattern bands
      </h3>
      <p className="mb-3">
        Each domain's percentage is also assigned to one of three descriptive
        bands: 0 to 33 percent, "quiet for now"; 34 to 66 percent, "active"; 67
        to 100 percent, "loud." The bands are a reading aid. They are not
        cutoffs and carry no clinical meaning.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        5.7 Severity
      </h3>
      <p className="mb-3">
        Severity is the sum of the duration and impact items, range 2 to 8. A
        score of 2 to 4 is reported as Early-Stage; 5 to 8 is reported as Deep.
        The two bands determine which next step the report recommends:
        Early-Stage results are routed to a self-paced workbook, Deep results to
        a facilitated six-week program. Severity is independent of the domain
        scores; a respondent can be Early-Stage with a strongly differentiated
        profile, or Deep with a flat one.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        5.8 Care flag
      </h3>
      <p className="mb-3">
        If the impact item is answered at its highest level ("I don't feel like
        myself at all anymore"), a care flag is set. The care flag does not
        change any score. It moves the care note (Section 8) from the bottom of
        the report to a highlighted position directly under the greeting.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        5.9 Worked example
      </h3>
      <p>
        A respondent chooses Boundaries on 7 screens, Belonging on 6, Beliefs on
        6, Body on 5, Budget on 4, and Business on 2 (total 30). Boundaries is
        primary at 70 percent. Belonging and Beliefs are tied for runner-up at
        60 percent; they met on two screens and Belonging won both, so Belonging
        is the runner-up. The runner-up is within two points of the primary, so
        the blend rule fires and the report leads with Boundaries, then explains
        how Belonging feeds it. The respondent answered duration 3 ("Over a
        year") and impact 2 ("It shows up every week"): severity 5, Deep, and
        the report recommends the facilitated program. Impact was not 4, so the
        care flag is not set and the care note sits at the bottom of the report.
      </p>
    </section>
  </div>
);

export default MethodologyScoring;
